
































































































import Vue from 'vue'
import SurfaceMapGoogleMarker from 'vuecomponents/surface_map_google_marker.vue'
import SurfaceMapConnection from 'vuecomponents/surface_map_connection.vue'
import BaseIcon from 'vuecomponents/base_icon.vue'
import { Component, Prop, Emit, Watch } from 'vue-property-decorator'
import {
  initMap,
  getUniquePositionsFromPositions,
  getBoundsContainingPositions,
  GeoMap,
  BoundsLiteral,
  Bounds,
  PostMarkerObject,
  getBoundsZoomLevel,
  ControlPosition,
  MarkerClusterer,
  OverlappingMarkerSpiderfier,
  MapEventsManager,
  MARKER_SHAPE_POLY_COORDS,
  PostConnectionObject,
  RELAXED_BOUND,
  RESTRICTED_BOUND,
  MapsEventListener,
} from 'bits/map'
import { getStylesFromThemeId, getMapTypeIdFromThemeId, getMarkerColorsFromThemeId } from 'bits/map_theme'
import { PinDropSuggestion, PostColor, Cid } from 'types'
import { namespace, State, Getter } from 'vuex-class'
import some from 'lodash.some'
import groupBy from 'lodash.groupby'
import debounce from 'lodash.debounce'

@Component({
  components: { SurfaceMapGoogleMarker, SurfaceMapConnection, BaseIcon },
})
class SurfaceMapGoogle extends Vue {
  $refs!: {
    control: HTMLElement
  }

  map: GeoMap | null = null
  mapBounds!: Bounds | BoundsLiteral
  mapConnections: PostConnectionObject[] = []
  mapFittedBoundsOnLoad = false
  resetMapViewportOnMinZoomListener?: MapsEventListener
  @Prop(String) backgroundColor
  @Prop(Number) latitude
  @Prop(Number) longitude
  @Prop(Number) zoom
  @Prop(Number) highlightedMarker
  @Prop(String) postBeingEditedCid
  @Prop({ type: Number, default: 100 }) themeId!: number
  @Prop(Array) markers!: PostMarkerObject[]
  @Prop({ type: Array, required: true }) connections!: PostConnectionObject[]
  @Getter isCommentable
  @Getter isReactable
  @State isMobile
  @Getter isMousable
  @Getter isPhone
  @Getter isApp
  @State dpr
  @Getter headerHeight
  @namespace('map').Getter isPreviewPanelShown
  @namespace('map').Getter offsetForMapPopup
  @namespace('map').Getter mapDoesFitMarkersBounds
  @namespace('map').Getter pinDropSuggestions
  @namespace('map').Getter pinDropLocation
  @namespace('map').Getter postWithPopupBeingOpenedCid
  @namespace('map').Getter postWithMarkerHighlightedCid
  @namespace('map').Getter markerClusterer
  @namespace('map').Getter isClusteringEnabled
  @namespace('postConnection').State postToConnectFromId
  @namespace('postConnection').State postToDisconnectFromId
  @namespace('map').Action enableMapFittingMarkersBounds
  @namespace('map').Action clearMapPinDropSuggestions
  @namespace('map').Action pickLocation
  @namespace('map').Action stopPickingLocation
  @namespace('map').Action setMarkerOverlayLayer
  @namespace('map').Action openPostPopup
  @namespace('map').Action closePostPopup
  @namespace('post').Action startNewPost
  @namespace('postConnection').Action completeDisconnection

  markerIcons: { fill; stroke } = getMarkerColorsFromThemeId(100)
  // clustererStyle: any[] = []
  mapMarkerRealWishStyle!: HTMLStyleElement

  get showMessage(): boolean {
    return !this.isMobile
  }

  get shouldClosePopup(): boolean {
    // Clicking outside an editing popup should not close the popup
    // What will happen is that the post will revert to view mode first
    // The next click will close the popup
    return !this.postBeingEditedCid
  }

  get isPopupTipDarkened(): boolean {
    // When the wall is commentable or reactable, we show a darkened
    // stalker bar at the bottom. In those cases, we need to darken
    // the tip to match the color of the stalker bar
    return this.isCommentable || this.isReactable
  }

  getFillColor(cid: Cid, color: PostColor): string {
    const { fill } = this.markerIcons
    const fillColor = fill[color || 'default']
    return fillColor
  }

  get markerShape(): google.maps.MarkerShape {
    return {
      coords: MARKER_SHAPE_POLY_COORDS,
      type: 'poly',
    }
  }

  /**
   * When markers are clustered, connections need to use starting/ending point of the cluster instead
   * of latlng of the post itselves. Also connections that have markers that are clustered together and connected to a same
   * other marker/cluster should be merged into one.
   * Mar 2022: Since we're not using cluster, this method is irrelevant since the function is guarded with the return on the
   * first if clause
   */
  generateMapConnections(clusterer: MarkerClusterer, connections: PostConnectionObject[]): PostConnectionObject[] {
    if (!clusterer) {
      return [...connections]
    }
    if (clusterer.getTotalClusters() === 0) {
      return []
    }
    const wishConnections = [...connections]
    const clusters = clusterer.getClusters()
    function checkClusterForMarkersInConnection(cluster, connection): { newFrom?; newTo? } {
      const clusterLat = cluster.getCenter().lat()
      const clusterLng = cluster.getCenter().lng()
      const fromPosition = connection.from.location_point
      const toPosition = connection.to.location_point
      let newFrom
      let newTo
      some(cluster.getMarkers(), (markerInCluster): boolean => {
        const markerInClusterPosition = markerInCluster.getPosition().toJSON()
        // Connection has FROM marker inside this cluster
        if (
          fromPosition.latitude === markerInClusterPosition.lat &&
          fromPosition.longitude === markerInClusterPosition.lng
        ) {
          // set FROM position to cluster center
          newFrom = {
            latitude: clusterLat,
            longitude: clusterLng,
          }
        }
        // Connection has TO marker inside this cluster
        if (
          toPosition.latitude === markerInClusterPosition.lat &&
          toPosition.longitude === markerInClusterPosition.lng
        ) {
          // set TO position to cluster center
          newTo = {
            latitude: clusterLat,
            longitude: clusterLng,
          }
        }
        // if both is assigned we stop searching
        if (!!newFrom && !!newTo) {
          return true
        }
        return false
      })
      return { newFrom, newTo }
    }
    function transformWishConnectionToMapConnection(connection: PostConnectionObject): PostConnectionObject {
      let newFromPosition, newToPosition
      // Check each clusters to see if the connection has clustered marker
      // and then change the connection to/from to cluster location
      some(clusters, (cluster): boolean => {
        const { newFrom, newTo } = checkClusterForMarkersInConnection(cluster, connection)
        if (newFrom) newFromPosition = newFrom
        if (newTo) newToPosition = newTo
        if (!!newFromPosition && !!newToPosition) {
          return true
        }
        return false
      })
      return {
        ...connection,
        from: {
          ...connection.from,
          location_point: newFromPosition || connection.from.location_point,
        },
        to: {
          ...connection.to,
          location_point: newToPosition || connection.to.location_point,
        },
      }
    }
    // Check and transform each wish connection to map connection
    let mapConnections = wishConnections.map(transformWishConnectionToMapConnection)
    // Remove self
    mapConnections = mapConnections.filter((connection: PostConnectionObject): boolean => {
      if (
        connection.from.location_point.latitude === connection.to.location_point.latitude &&
        connection.from.location_point.longitude === connection.to.location_point.longitude
      ) {
        // self
        return false
      }
      return true
    })
    // Group identical connections
    const mapConnectionsGroup = groupBy(mapConnections, (connection): string => {
      const toLat = connection.to.location_point.latitude
      const toLng = connection.to.location_point.longitude
      const fromLat = connection.from.location_point.latitude
      const fromLng = connection.from.location_point.longitude
      const pointA = toLat <= fromLat ? `${toLat},${toLng}` : `${fromLat},${fromLng}`
      const pointB = toLat > fromLat ? `${toLat},${toLng}` : `${fromLat},${fromLng}`
      return `${pointA}:${pointB}`
    })
    const distinctMapConnections = Object.keys(mapConnectionsGroup).map((key): PostConnectionObject => {
      const connectionGroup = mapConnectionsGroup[key]
      if (connectionGroup.length === 1) {
        return connectionGroup[0]
      }
      return {
        from: connectionGroup[0].from,
        to: connectionGroup[0].to,
        id: connectionGroup[0].id,
        label: undefined,
        labelCount: connectionGroup.length,
      }
    })
    return distinctMapConnections
  }

  @Watch('markers', { immediate: true })
  respondToMarkersChanged(): void {
    // Fit the map immediately after the map receives markers at the first time
    if (!this.mapFittedBoundsOnLoad) {
      this.$nextTick(() => {
        this.fitBounds()
        this.mapFittedBoundsOnLoad = true
      })
    } else if (this.mapDoesFitMarkersBounds) {
      // Fit the map on markers changed only if we allow the map to fit
      // Example of some cases where we do not want it to fit:
      // - User is adding a post
      // - User is editing a post
      this.fitBounds()
    }
  }

  @Watch('connections', { immediate: true })
  respondToWishConnections(connections): void {
    this.mapConnections = this.generateMapConnections(this.markerClusterer, connections)
  }

  @Watch('postBeingEditedCid')
  onPostBeingEditedCidChanged(val): void {
    if (val) {
      this.$nextTick(() => {
        this.openPostPopup({ postCid: val })
      })
    }
  }

  get isAPopupOpen(): boolean {
    return !!this.postWithPopupBeingOpenedCid || (this.pinDropSuggestions && this.pinDropSuggestions.length > 0)
  }

  @Watch('isAPopupOpen')
  respondToIsAPopupOpen(isAPopupOpen) {
    if (isAPopupOpen) {
      this.map && this.map.setOptions({ restriction: RELAXED_BOUND })
    } else {
      this.map && this.map.setOptions({ restriction: RESTRICTED_BOUND })
    }
  }

  @Watch('highlightedMarker')
  respondToHighlightedMarkerChanged(highlightedMarker): void {
    this.markers.forEach((m, index) => {
      if (index === highlightedMarker) {
        // m[1].setAsSelecte
      } else {
        // m[1].unsetAsSelected()
      }
    })
  }

  @Watch('themeId')
  respondToThemeIdChanged(themeId): void {
    this.getMarkerAssets(themeId)
    this.map &&
      this.map.setOptions({
        styles: getStylesFromThemeId(themeId),
        mapTypeId: getMapTypeIdFromThemeId(themeId),
      })
  }

  @Watch('latitude')
  @Watch('longitude')
  @Watch('zoom')
  respondToStartingLocationChanged(): void {
    if (this.markers.length === 0) {
      this.map && this.map.setCenter({ lat: this.latitude, lng: this.longitude })
      this.map && this.map.setZoom(this.zoom)
    }
  }

  // @Watch('clustererStyle')
  // respondToClustererStyle(): void {
  //   if (!this.markerClusterer) return
  //   // this.markerClusterer.setStyles(clustererStyle)
  //   // Delay repainting after other attributes are changed, low priority
  //   this.$nextTick(() => {
  //     setTimeout(() => {
  //       this.markerClusterer.repaint()
  //     }, 200)
  //   })
  // }

  @Watch('offsetForMapPopup')
  respondToOffsetForMapPopupChanged(offsetForMapPopup: ClientRect): void {
    this.mapMarkerRealWishStyle.innerHTML = `
    .real-wish, .map-wish {
        max-height: calc(100vh - ${offsetForMapPopup.top + offsetForMapPopup.bottom}px);
    }
    .map-wish-with-stalker-bar {
      max-height: calc(100vh - ${offsetForMapPopup.top + offsetForMapPopup.bottom + 38}px);
    }
    `
  }

  @Emit('click')
  click(e): Event {
    if (this.pinDropLocation) {
      this.clearMapPinDropSuggestions()
      this.stopPickingLocation()
    }
    if (this.shouldClosePopup) {
      this.closePostPopup()
    }
    if (e.placeId) {
      // Ignore click on places of interest
      e.stop()
    }
    return e
  }

  @Emit('initialized')
  emitInitializeEvent(map): GeoMap {
    return map
  }

  addPostWithSelectedPinDropSuggestion(pinDropSuggestion: PinDropSuggestion): void {
    this.pickLocation({ latLng: this.pinDropLocation, name: pinDropSuggestion.locationName })
    this.clearMapPinDropSuggestions()
  }

  getMarkerAssets(themeId): void {
    const markerIcons = getMarkerColorsFromThemeId(themeId || 100)
    this.markerIcons = markerIcons
    // this.clustererStyle = [
    //   // { url: (await import(`images/markers/clusters/${markerIcons.fill.default}/m1`)).default, width: 52, height: 52, textColor: this.labelColor },
    //   // { url: (await import(`images/markers/clusters/${markerIcons.fill.default}/m2`)).default, width: 56, height: 56, textColor: this.labelColor },
    //   // { url: (await import(`images/markers/clusters/${markerIcons.fill.default}/m3`)).default, width: 66, height: 66, textColor: this.labelColor },
    //   // { url: (await import(`images/markers/clusters/${markerIcons.fill.default}/m4`)).default, width: 77, height: 77, textColor: this.labelColor },
    //   // { url: (await import(`images/markers/clusters/${markerIcons.fill.default}/m5`)).default, width: 91, height: 91, textColor: this.labelColor },
    // ]
  }

  mounted(): void {
    this.getMarkerAssets(this.themeId)

    this.map = initMap(this.$el, this.latitude, this.longitude, this.zoom, {
      zoomControl: false,
      minZoom: this.isMobile ? undefined : 2,
    })
    this.map.addListener('click', (e) => {
      this.click(e)
    })
    //
    const mapControlLocation = this.isPhone || this.isApp ? ControlPosition.LEFT_BOTTOM : ControlPosition.RIGHT_BOTTOM
    this.map.controls[mapControlLocation].push(this.$refs.control)
    this.map.setOptions({
      styles: getStylesFromThemeId(this.themeId),
      mapTypeId: getMapTypeIdFromThemeId(this.themeId),
      backgroundColor: this.backgroundColor,
    })
    this.emitInitializeEvent(this.map)
    let markerClusterer: MarkerClusterer | undefined = undefined
    if (this.isClusteringEnabled) {
      markerClusterer = new MarkerClusterer(this.map, [], {
        maxZoom: 15,
        enableRetinaIcons: true,
        zoomOnClick: false, // handle zooming on our own
      })
      MapEventsManager.addListener(markerClusterer, 'click', (cluster) => {
        const positions = cluster.getMarkers().map((marker) => marker.getPosition())
        this.fitMapForPositions(positions, true)
      })

      this.$nextTick(() => {
        if (!this.markerClusterer) return
        // Repaint the markerclusterer after all markers are mounted
        // to fix an issue whereby all marker are clustered on loaded
        this.markerClusterer.repaint()
        this.map &&
          MapEventsManager.addListener(this.map, 'idle', () => {
            this.markerClusterer.repaint()
          })
      })
      MapEventsManager.addListener(markerClusterer, 'clusteringend', (c) => {
        if (this.isClusteringEnabled) {
          this.mapConnections = this.generateMapConnections(c, this.connections)
        }
      })
    }
    this.setMarkerOverlayLayer({
      spiderfier: new OverlappingMarkerSpiderfier(this.map, {
        circleFootSeparation: 30,
        keepSpiderfied: true,
        nearbyDistance: 1, // markers that are maximum 1px apart will be spiderfied
      }),
      clusterer: markerClusterer,
    })
    this.$nextTick(() => {
      this.mapMarkerRealWishStyle = document.createElement('style')
      this.mapMarkerRealWishStyle.innerHTML = `
      .real-wish, .map-wish {
        max-height: calc(100vh - ${this.offsetForMapPopup.top + this.offsetForMapPopup.bottom}px);
      }
      .map-wish-with-stalker-bar {
        max-height: calc(100vh - ${this.offsetForMapPopup.top + this.offsetForMapPopup.bottom + 38}px);
      }
      `
      document.head.appendChild(this.mapMarkerRealWishStyle)
    })

    MapEventsManager.addListener(this.map, 'zoom_changed', () => {
      if (this.isClusteringEnabled) {
        this.mapConnections = []
      }
    })
  }

  openMarkerPopup(postCid): void {
    this.openPostPopup({ postCid })
  }

  closeMarkerPopup(postCid): void {
    if (this.postWithPopupBeingOpenedCid === postCid) {
      this.closePostPopup()
    }
  }

  /**
   * We always fit the map inside one horizontal map with the America on the left
   * and Asia on the right if the map is in zoomed out to the world level
   * IDL = International Date Line
   */
  setupFittingMapInsideIDLBounds() {
    this.resetMapViewportOnMinZoomListener && this.resetMapViewportOnMinZoomListener.remove()
    const resetMapViewportToCenterOnMinZoom: Function = debounce(() => {
      if (!this.map) return
      const bounds = this.map.getBounds()
      if (!bounds) return
      // Only when map is zoomed out to the world level
      if (bounds.getNorthEast().lng() === 180 && bounds.getSouthWest().lng() === -180) {
        let longitude = this.map.getCenter().lng() % 360
        if (longitude < -180) longitude = 360 + longitude
        // Move the map back into center if user pan outside
        if (longitude < -90 || longitude > 90) {
          this.map.panTo({
            lat: this.map.getCenter().lat(),
            lng: 0,
          })
          this.resetMapViewportOnMinZoomListener && this.resetMapViewportOnMinZoomListener.remove()
          // Continue listening
          MapEventsManager.addListenerOnce(this.map, 'idle', listenToBoundsChanged)
        }
      }
    }, 100)
    const listenToBoundsChanged = () => {
      if (!this.map) return
      this.resetMapViewportOnMinZoomListener = MapEventsManager.addListener(this.map, 'bounds_changed', (e) => {
        if (!this.isAPopupOpen) {
          resetMapViewportToCenterOnMinZoom()
        }
      })
    }
    listenToBoundsChanged()
  }

  fitMapForPositions(positions, pan = false) {
    if (!this.map) {
      return
    }
    const mapDim = {
      height: this.$el.clientHeight || window.innerHeight - this.headerHeight,
      width: this.$el.clientWidth,
    }
    this.mapBounds = getBoundsContainingPositions(positions)
    const boundsZoomLevel = getBoundsZoomLevel(this.mapBounds, mapDim)

    this.setupFittingMapInsideIDLBounds()

    this.map.setZoom(boundsZoomLevel)
    // Uncomment to see the bounds that the map is being fitted to
    // new google.maps.Rectangle({ bounds: this.mapBounds, map: this.map })
    if (pan) {
      this.map.panTo(this.mapBounds.getCenter())
    } else {
      this.map.setCenter(this.mapBounds.getCenter())
    }
    // Doing fitBounds because center is on the globe using lat and lng is not the center of
    // the bound rectangle. Pan to center will be off on our map with Mercator projection
    this.map.fitBounds(this.mapBounds)
  }

  fitBounds(): void {
    if (!this.map) {
      return
    }
    const positions = getUniquePositionsFromPositions(this.markers.map((m) => m.position))
    if (positions.length > 1) {
      this.fitMapForPositions(positions)
    } else if (positions.length === 1) {
      this.map.setZoom(16)
      this.map.setCenter(positions[0])
    } else {
      this.map.setZoom(this.zoom)
      this.map.setCenter({ lat: this.latitude, lng: this.longitude })
    }
  }

  zoomIn(): void {
    if (!this.map) {
      return
    }
    this.map.setZoom(this.map.getZoom() + 1)
  }

  zoomOut(): void {
    if (!this.map) {
      return
    }
    this.map.setZoom(this.map.getZoom() - 1)
  }
}
export default SurfaceMapGoogle
